import * as React from 'react';
import { Check, ChevronsUpDown, X, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { getManufacturers, getModels } from '../../api';

/**
 * ManufacturerModelSelect - A searchable multi-select component for manufacturers and models
 *
 * Allows selecting manufacturers and/or models independently.
 * Models are grouped by their manufacturer.
 *
 * @param {Object} props
 * @param {Array} props.manufacturerIds - Array of selected manufacturer IDs
 * @param {Array} props.modelIds - Array of selected model IDs
 * @param {Function} props.onManufacturerChange - Callback when manufacturer selection changes
 * @param {Function} props.onModelChange - Callback when model selection changes
 * @param {boolean} props.disabled - Disable the component
 */
export function ManufacturerModelSelect({
	manufacturerIds = [],
	modelIds = [],
	onManufacturerChange,
	onModelChange,
	disabled = false,
	className,
}) {
	const [manufacturers, setManufacturers] = React.useState([]);
	const [models, setModels] = React.useState([]);
	const [loading, setLoading] = React.useState(true);
	const [open, setOpen] = React.useState(false);
	const [search, setSearch] = React.useState('');
	const [activeTab, setActiveTab] = React.useState('manufacturers');
	const containerRef = React.useRef(null);
	const inputRef = React.useRef(null);

	// Fetch manufacturers and models on mount
	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const [mfgResponse, modelResponse] = await Promise.all([
					getManufacturers(),
					getModels(),
				]);
				setManufacturers(mfgResponse?.rows || mfgResponse || []);
				setModels(modelResponse?.rows || modelResponse || []);
			} catch (error) {
				console.error('Failed to fetch manufacturers/models:', error);
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, []);

	// Close dropdown when clicking outside
	React.useEffect(() => {
		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Filter manufacturers based on search
	const filteredManufacturers = React.useMemo(() => {
		if (!search) return manufacturers;
		const term = search.toLowerCase();
		return manufacturers.filter((m) => m.name?.toLowerCase().includes(term));
	}, [manufacturers, search]);

	// Filter models based on search and group by manufacturer
	const filteredModels = React.useMemo(() => {
		let filtered = models;
		if (search) {
			const term = search.toLowerCase();
			filtered = models.filter(
				(m) =>
					m.name?.toLowerCase().includes(term) ||
					m.manufacturer?.name?.toLowerCase().includes(term)
			);
		}
		return filtered;
	}, [models, search]);

	// Group models by manufacturer
	const groupedModels = React.useMemo(() => {
		const groups = {};
		filteredModels.forEach((model) => {
			const mfgName = model.manufacturer?.name || 'Other';
			if (!groups[mfgName]) {
				groups[mfgName] = [];
			}
			groups[mfgName].push(model);
		});
		return groups;
	}, [filteredModels]);

	// Get selected items for display
	const selectedManufacturers = React.useMemo(() => {
		return manufacturers.filter((m) => manufacturerIds.includes(m.id));
	}, [manufacturers, manufacturerIds]);

	const selectedModels = React.useMemo(() => {
		return models.filter((m) => modelIds.includes(m.id));
	}, [models, modelIds]);

	const handleManufacturerSelect = (id) => {
		const newValue = manufacturerIds.includes(id)
			? manufacturerIds.filter((mId) => mId !== id)
			: [...manufacturerIds, id];
		onManufacturerChange(newValue);
	};

	const handleModelSelect = (id) => {
		const newValue = modelIds.includes(id)
			? modelIds.filter((mId) => mId !== id)
			: [...modelIds, id];
		onModelChange(newValue);
	};

	const handleRemoveManufacturer = (id, e) => {
		e.stopPropagation();
		onManufacturerChange(manufacturerIds.filter((mId) => mId !== id));
	};

	const handleRemoveModel = (id, e) => {
		e.stopPropagation();
		onModelChange(modelIds.filter((mId) => mId !== id));
	};

	const handleClearAll = (e) => {
		e.stopPropagation();
		onManufacturerChange([]);
		onModelChange([]);
	};

	const handleTriggerClick = () => {
		if (!disabled) {
			setOpen(!open);
			if (!open) {
				setTimeout(() => inputRef.current?.focus(), 0);
			}
		}
	};

	const totalSelected = selectedManufacturers.length + selectedModels.length;

	if (loading) {
		return (
			<div className={cn('w-full', className)}>
				<div className="flex min-h-[42px] w-full items-center justify-center rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-400">
					Loading manufacturers and models...
				</div>
			</div>
		);
	}

	return (
		<div className={cn('w-full relative', className)} ref={containerRef}>
			{/* Trigger button */}
			<button
				type="button"
				role="combobox"
				aria-expanded={open}
				onClick={handleTriggerClick}
				className={cn(
					'flex min-h-[42px] w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm ring-offset-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
					totalSelected > 0 && 'h-auto'
				)}
				disabled={disabled}
			>
				<div className="flex flex-1 flex-wrap gap-1.5">
					{totalSelected === 0 ? (
						<span className="text-gray-400">Select manufacturers or models...</span>
					) : (
						<>
							{selectedManufacturers.map((mfg) => (
								<Badge
									key={`mfg-${mfg.id}`}
									variant="secondary"
									className="flex items-center gap-1 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
								>
									<span className="max-w-[150px] truncate">{mfg.name}</span>
									<button
										type="button"
										className="ml-1 rounded-full outline-none ring-offset-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
										onClick={(e) => handleRemoveManufacturer(mfg.id, e)}
									>
										<X className="h-3 w-3 text-blue-600 hover:text-blue-800" />
									</button>
								</Badge>
							))}
							{selectedModels.map((model) => (
								<Badge
									key={`model-${model.id}`}
									variant="secondary"
									className="flex items-center gap-1 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
								>
									<span className="max-w-[150px] truncate">
										{model.manufacturer?.name} {model.name}
									</span>
									<button
										type="button"
										className="ml-1 rounded-full outline-none ring-offset-white focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
										onClick={(e) => handleRemoveModel(model.id, e)}
									>
										<X className="h-3 w-3 text-purple-600 hover:text-purple-800" />
									</button>
								</Badge>
							))}
						</>
					)}
				</div>
				<div className="flex items-center gap-1 ml-2">
					{totalSelected > 0 && (
						<button
							type="button"
							className="rounded-full p-0.5 hover:bg-gray-100"
							onClick={handleClearAll}
						>
							<X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
						</button>
					)}
					<ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
				</div>
			</button>

			{/* Dropdown */}
			{open && (
				<div className="absolute z-[100] mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
					{/* Search input */}
					<div className="flex items-center border-b px-3">
						<Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
						<input
							ref={inputRef}
							type="text"
							className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-gray-400"
							placeholder="Search..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>

					{/* Tabs */}
					<div className="flex border-b">
						<button
							type="button"
							onClick={() => setActiveTab('manufacturers')}
							className={cn(
								'flex-1 px-4 py-2 text-sm font-medium transition-colors',
								activeTab === 'manufacturers'
									? 'border-b-2 border-indigo-500 text-indigo-600'
									: 'text-gray-500 hover:text-gray-700'
							)}
						>
							<div className="flex items-center justify-center gap-1.5">
								Manufacturers
								{selectedManufacturers.length > 0 && (
									<span className="ml-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-xs text-blue-600">
										{selectedManufacturers.length}
									</span>
								)}
							</div>
						</button>
						<button
							type="button"
							onClick={() => setActiveTab('models')}
							className={cn(
								'flex-1 px-4 py-2 text-sm font-medium transition-colors',
								activeTab === 'models'
									? 'border-b-2 border-indigo-500 text-indigo-600'
									: 'text-gray-500 hover:text-gray-700'
							)}
						>
							<div className="flex items-center justify-center gap-1.5">
								Models
								{selectedModels.length > 0 && (
									<span className="ml-1 rounded-full bg-purple-100 px-1.5 py-0.5 text-xs text-purple-600">
										{selectedModels.length}
									</span>
								)}
							</div>
						</button>
					</div>

					{/* Content */}
					<div className="max-h-[300px] overflow-y-auto p-1">
						{activeTab === 'manufacturers' ? (
							filteredManufacturers.length === 0 ? (
								<p className="py-6 text-center text-sm text-gray-500">
									No manufacturers found.
								</p>
							) : (
								filteredManufacturers.map((mfg) => {
									const isSelected = manufacturerIds.includes(mfg.id);
									return (
										<button
											key={mfg.id}
											type="button"
											onClick={() => handleManufacturerSelect(mfg.id)}
											className="relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100"
										>
											<div
												className={cn(
													'flex h-4 w-4 items-center justify-center rounded border',
													isSelected
														? 'border-blue-600 bg-blue-600 text-white'
														: 'border-gray-300'
												)}
											>
												{isSelected && <Check className="h-3 w-3" />}
											</div>
											<span className="font-medium">{mfg.name}</span>
										</button>
									);
								})
							)
						) : Object.keys(groupedModels).length === 0 ? (
							<p className="py-6 text-center text-sm text-gray-500">No models found.</p>
						) : (
							Object.entries(groupedModels).map(([mfgName, mfgModels]) => (
								<div key={mfgName} className="mb-2">
									<div className="px-2 py-1.5 text-xs font-medium text-gray-500">
										{mfgName}
									</div>
									{mfgModels.map((model) => {
										const isSelected = modelIds.includes(model.id);
										return (
											<button
												key={model.id}
												type="button"
												onClick={() => handleModelSelect(model.id)}
												className="relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100 ml-2"
											>
												<div
													className={cn(
														'flex h-4 w-4 items-center justify-center rounded border',
														isSelected
															? 'border-purple-600 bg-purple-600 text-white'
															: 'border-gray-300'
													)}
												>
													{isSelected && <Check className="h-3 w-3" />}
												</div>
												<span className="font-medium">{model.name}</span>
											</button>
										);
									})}
								</div>
							))
						)}
					</div>
				</div>
			)}

			{/* Help text */}
			<p className="mt-1.5 text-xs text-gray-500">
				Select manufacturers for general applicability, or specific models for targeted SOPs.
			</p>
		</div>
	);
}

export default ManufacturerModelSelect;
